import { Outlet } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Sidebar } from '../../components/Sidebar'
import { AccountMenu } from '../../components/AccountMenu'
import { AppShellLayout } from '../../components/AppShellLayout'
import { SubscriptionStrip } from '../wallet/SubscriptionStrip'
import { ShellHeader } from '../../components/ShellHeader'
import { StoreSelector } from '../../components/StoreSelector'
import { useMe } from '../../lib/useMe'
import { usePriceMaskSync } from '../../lib/usePriceMaskSync'
import { useT } from '../../i18n'
import { workspaceTasksItem } from '../../lib/workspaceSidebar'
import { useTaskBadge } from '../staff-tasks/useTaskBadge'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import { sellerShellI18n } from '../../i18n/seller/shell'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { computeTabCounts, type SellerOrderTabSummary } from './order-tabs'
import { buildAwaitingShipmentStoreItems } from './awaiting-shipment-stores'
import { fetchSellerStores } from '../../api/seller-listings'
import { messagingI18n } from '../../i18n/messaging'
import { useMessagingCapabilities } from '../shared/messaging/useMessagingCapabilities'
import { TenantMessagingLauncher } from '../shared/messaging/TenantMessagingLauncher'
import { useMessagingUnread } from '../shared/messaging/useMessagingUnread'
import { useMessagingRealtime } from '../shared/messaging/useMessagingRealtime'
import { SellerHome as AdaptiveSellerHome } from './home/SellerHome'
import { SellerTourProvider } from '../onboarding/SellerOnboardingTour'
import { useHasSellerServers } from './servers/useSellerServers'

export function SellerShell() {
  const t = useT(sellerShellI18n)
  const tm = useT(messagingI18n)
  const tTasks = useT(staffTasksI18n)
  const taskBadge = useTaskBadge()
  const { isSellerStaff } = useMe()
  // Shown only to a seller Pexify has given a machine; everyone else never sees it.
  const hasServers = useHasSellerServers()
  usePriceMaskSync()
  const messagingCapabilities = useMessagingCapabilities()
  const messagingUnread = useMessagingUnread(
    'seller',
    messagingCapabilities.can_view_inbox,
  )
  useMessagingRealtime('seller', messagingCapabilities.can_view_inbox)
  // Real-mode sidebar count badges come from the whole-org summary aggregate,
  // NOT from order rows — the list pages at 200 rows, so counting one page
  // silently truncated the badges once the org outgrew the window (PR #366
  // known risk). The cache entry is shared with My Orders, and mutations that
  // invalidate ['seller','orders'] refresh it by prefix.
  const ordersSummary = useQuery({
    queryKey: ['seller', 'orders', 'summary'],
    queryFn: async () => {
      const res = await fetch(apiUrl('/seller/orders/summary'))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json() as Promise<SellerOrderTabSummary>
    },
    enabled: USE_REAL_API,
  })
  const counts = ordersSummary.data?.tabs ?? computeTabCounts([])
  // Same cache entry as the My Orders sync popover (['seller','stores']).
  const sellerStores = useQuery({
    queryKey: ['seller', 'stores'],
    queryFn: fetchSellerStores,
    enabled: USE_REAL_API,
  })
  const awaitingShipmentStores = buildAwaitingShipmentStoreItems(
    sellerStores.data?.items ?? [],
    ordersSummary.data?.awaiting_shipment_by_store ?? [],
    t.items.otherStoreOrders,
  )

  return (
    <SellerTourProvider>
      <AppShellLayout
        contextBar={isSellerStaff ? null : <SubscriptionStrip />}
        sidebar={
          <Sidebar
            title={t.title}
            layout="aligned"
            header={<ShellHeader selector={<StoreSelector />} />}
            headerIcon="navStores"
            items={[
              { to: '/seller', label: t.items.overview, icon: 'navOverview' },
              ...(messagingCapabilities.can_view_inbox
                ? [
                    {
                      to: '/seller/messages',
                      label: tm.navigation.seller,
                      icon: 'messages' as const,
                      badge: messagingUnread.unreadMessages,
                    },
                  ]
                : []),
              {
                to: '/seller/my-orders?status=awaiting_shipment',
                label: t.items.myOrders,
                icon: 'navOrders',
                groupId: 'seller-orders',
              },
              {
                to: '/seller/my-orders?status=awaiting_shipment',
                label: t.items.awaitingShipment,
                parentGroupId: 'seller-orders',
                badge: counts.awaiting_shipment,
                subItems: awaitingShipmentStores,
              },
              {
                to: '/seller/my-orders?status=awaiting_payment',
                label: t.items.awaitingPayment,
                parentGroupId: 'seller-orders',
                badge: counts.awaiting_payment,
              },
              {
                to: '/seller/my-orders?status=shipped',
                label: t.items.shipped,
                parentGroupId: 'seller-orders',
                badge: counts.shipped,
              },
              {
                to: '/seller/my-orders?status=on_hold',
                label: t.items.onHold,
                parentGroupId: 'seller-orders',
                badge: counts.on_hold,
              },
              {
                to: '/seller/my-orders?status=cancelled',
                label: t.items.cancelled,
                parentGroupId: 'seller-orders',
                badge: counts.cancelled,
              },
              {
                to: '/seller/my-orders?status=all',
                label: t.items.allOrders,
                parentGroupId: 'seller-orders',
              },
              {
                to: '/seller/returns',
                label: t.items.returns,
                parentGroupId: 'seller-orders',
              },
              {
                to: '/seller/catalog',
                label: t.items.catalogue,
                icon: 'navCatalog',
              },
              {
                to: '/seller/_group_products',
                label: t.items.productsGroup,
                icon: 'navProducts',
                groupId: 'seller-products',
              },
              {
                to: '/seller/my-products',
                // Path-only match so filters/prefill in the query keep the row lit
                // (e.g. /seller/my-listings?status=issues from Home).
                alsoActiveFor: ['/seller/my-products'],
                label: t.items.products,
                icon: 'navProducts',
                parentGroupId: 'seller-products',
              },
              {
                to: '/seller/my-listings',
                alsoActiveFor: ['/seller/my-listings'],
                label: t.items.listings,
                icon: 'navListings',
                parentGroupId: 'seller-products',
              },
              {
                to: '/seller/inventory',
                alsoActiveFor: ['/seller/inventory'],
                label: t.items.inventory,
                icon: 'navInventory',
                parentGroupId: 'seller-products',
              },
              {
                to: '/seller/my-reports',
                alsoActiveFor: ['/seller/my-reports'],
                label: t.items.productReports,
                icon: 'fileSearch',
                parentGroupId: 'seller-products',
              },
              {
                to: '/seller/_group_stores',
                label: t.items.storesGroup,
                icon: 'navStores',
                tourTarget: 'seller-stores-nav',
                groupId: 'seller-stores',
              },
              {
                to: '/seller/stores',
                label: t.items.myStores,
                icon: 'navStores',
                parentGroupId: 'seller-stores',
              },
              {
                to: '/seller/scorecard',
                label: t.items.scorecard,
                icon: 'navInsights',
                parentGroupId: 'seller-stores',
              },
              {
                to: '/seller/reviews',
                label: t.items.reviews,
                icon: 'favorite',
                parentGroupId: 'seller-stores',
              },
              ...(hasServers
                ? [
                    {
                      to: '/seller/servers',
                      label: t.items.servers,
                      icon: 'servers' as const,
                    },
                  ]
                : []),
              {
                to: '/seller/_group_reports',
                label: t.items.reportsGroup,
                icon: 'navInsights',
                groupId: 'seller-reports',
              },
              {
                to: '/seller/multi-store-report',
                label: t.items.storePerformance,
                icon: 'navInsights',
                parentGroupId: 'seller-reports',
              },
              {
                to: '/seller/activity',
                label: t.items.activityHistory,
                icon: 'activity',
                parentGroupId: 'seller-reports',
              },
              {
                to: '/seller/_group_tools',
                label: t.items.toolsGroup,
                icon: 'calculator',
                groupId: 'seller-tools',
              },
              {
                to: '/seller/tools/etsy-profit',
                label: t.items.etsyProfitCalc,
                icon: 'calculator',
                parentGroupId: 'seller-tools',
              },
              {
                to: '/seller/tools/etsy-trends',
                label: t.items.etsyTrends,
                icon: 'insights',
                parentGroupId: 'seller-tools',
              },
              {
                to: '/seller/tools/us-calendar',
                label: t.items.usCalendar,
                icon: 'calendar',
                parentGroupId: 'seller-tools',
              },
              {
                to: '/seller/scenes',
                label: t.items.scenes,
                icon: 'sparkle',
                parentGroupId: 'seller-tools',
              },
              ...(isSellerStaff
                ? []
                : [
                    {
                      to: '/seller/wallet',
                      label: t.items.walletGroup,
                      icon: 'navWallet' as const,
                      groupId: 'wallet-seller',
                    },
                    {
                      to: '/seller/wallet/payments',
                      label: t.items.balance,
                      icon: 'payments' as const,
                      parentGroupId: 'wallet-seller',
                    },
                    {
                      to: '/seller/wallet/add-funds',
                      label: t.items.addFunds,
                      icon: 'addFunds' as const,
                      parentGroupId: 'wallet-seller',
                    },
                    {
                      to: '/seller/wallet/subscription',
                      label: t.items.subscription,
                      icon: 'receipt' as const,
                      parentGroupId: 'wallet-seller',
                    },
                    {
                      to: '/seller/wallet/payouts',
                      label: t.items.stripePayouts,
                      icon: 'banking' as const,
                      parentGroupId: 'wallet-seller',
                    },
                    {
                      to: '/seller/wallet/banking',
                      label: t.items.bankAccount,
                      icon: 'banking' as const,
                      parentGroupId: 'wallet-seller',
                    },
                    {
                      to: '/seller/wallet/referral',
                      label: t.items.referral,
                      icon: 'referral' as const,
                      parentGroupId: 'wallet-seller',
                    },
                  ]),
              { to: '/seller/settings-divider', label: '', isHeader: true },
              { to: '/seller/drive', label: t.items.myFiles, icon: 'cloud' },
              { to: '/seller/resources', label: t.items.help, icon: 'learning' },
              { to: '/seller/settings', label: t.items.settings, icon: 'navSettings' },
              // Workspace: Tasks — each seller team's own to-do list.
              {
                to: '/seller/workspace-header',
                label: t.items.workspace,
                isHeader: true as const,
              },
              workspaceTasksItem('/seller', { label: tTasks.navLabel, badge: taskBadge }),
            ]}
            footer={<AccountMenu shellBase="/seller" fallbackName="Seller (demo)" />}
          />
        }
      >
        <Outlet />
      </AppShellLayout>
      <TenantMessagingLauncher surface="seller" />
    </SellerTourProvider>
  )
}

export function SellerHome() {
  return <AdaptiveSellerHome />
}
